/**
 * Financial records extracted from investment documents.
 *
 * When a document classifies as investment-related (investments, fund_admin,
 * fund_accounting, banking), a second LLM pass pulls structured money facts
 * into `financialRecords/{id}`. Jon reviews/edits them in the Financials ledger;
 * over time these become the spine of the SFP accounting system.
 */
export const FINANCIAL_KINDS = [
    'commitment', // capital committed to a fund
    'capital_call', // a drawdown against a commitment
    'distribution', // cash/stock returned from a fund
    'capital_account', // PCAP / NAV / ending capital balance
    'carried_interest', // carry earned or allocated
    'management_fee', // fee paid/charged
    'gift', // a gift made/received (incl. its value)
    'loan', // a loan made/received
    'valuation', // a standalone valuation of an asset/interest
    'contribution', // non-fund capital contribution
    'other',
];
export const FINANCIAL_KIND_LABEL = {
    commitment: 'Commitment',
    capital_call: 'Capital Call',
    distribution: 'Distribution',
    capital_account: 'Capital Account (PCAP)',
    carried_interest: 'Carried Interest',
    management_fee: 'Management Fee',
    gift: 'Gift',
    loan: 'Loan',
    valuation: 'Valuation',
    contribution: 'Contribution',
    other: 'Other',
};
/** Categories whose docs trigger financial extraction. */
export const FINANCIAL_SOURCE_CATEGORIES = [
    'investments',
    'fund_admin',
    'fund_accounting',
    'banking',
];
export function isFinancialSourceCategory(category) {
    return !!category && FINANCIAL_SOURCE_CATEGORIES.includes(category);
}
/** System prompt for the financial-extraction LLM pass. */
export const FINANCIAL_EXTRACT_PROMPT = `You are a private-family-office analyst extracting structured financial facts from a document.

Return ONLY a JSON object: { "facts": [ ... ] }. Each fact is an object with:
  "kind": one of ${FINANCIAL_KINDS.join(', ')}.
  "amountUsd": number — the dollar amount (NOT cents), e.g. 1500000.50. Use null if only a percentage is given.
  "date": string — ISO 8601 date the figure is as-of/effective, or "".
  "entity": string — the Shulkin-side entity or person this figure belongs to (e.g. "JKS Revocable Trust").
  "fund": string — the fund/vehicle/investment it relates to, or "".
  "counterparty": string — for a gift: giver→recipient; for a loan: lender/borrower; for a fund line: the manager; else "".
  "percentage": number or null — carry %, ownership %, or interest rate when stated.
  "description": string — one short phrase describing the line item.

Rules:
- Only extract figures actually present. Do NOT invent or estimate. If none, return { "facts": [] }.
- A capital account statement (PCAP) typically yields: commitment, contributions/capital_call, distributions, and an ending capital_account (NAV) — emit each present figure as its own fact.
- For gifts, capture the gift AND its value as a single 'gift' fact with amountUsd = the value.
- Prefer the most specific kind. Use 'other' only when nothing fits.
- amounts are USD unless the document clearly says otherwise.`;
//# sourceMappingURL=financial.js.map